import { useMemo, useState, type ReactNode } from "react";
import { create } from "@bufbuild/protobuf";
import { Code } from "@connectrpc/connect";
import { createConnectQueryKey, useMutation, useQuery } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { create as createQr } from "qrcode";

import { formatAdDate, formatDate, formatNepalTime } from "@yatayat/core/date";
import { formatNumber, formatPaisa } from "@yatayat/core/money";
import {
  BookingService,
  GetTicketResponseSchema,
  TicketStatus,
  type CancellationQuote,
  type Ticket,
} from "@yatayat/gen/yatayat/booking/v1/booking_pb";

import { PhoneSignIn } from "~/components/PhoneSignIn";
import { TicketStatusBadge } from "~/components/TicketStatusBadge";
import { TripSafety } from "~/components/TripSafety";
import { useI18n, type MessageKey } from "~/lib/i18n";
import { localDigits, paymentMethodLabels, placeName, vehicleClassLabels } from "~/lib/labels";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { useRefreshSession } from "~/lib/session";

export const Route = createFileRoute("/tickets/$ticketId")({
  component: TicketPage,
});

function TicketPage() {
  const { ticketId } = Route.useParams();
  const { t } = useI18n();
  const query = useQuery(BookingService.method.getTicket, { id: ticketId }, { retry: retryTransient });
  const ticket = query.data?.ticket;

  let body: ReactNode;
  if (ticket) {
    body = <TicketView ticket={ticket} />;
  } else if (query.isError) {
    switch (errorCode(query.error)) {
      case Code.Unauthenticated:
        body = (
          <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
            <PhoneSignIn intro={t("signInForTickets")} />
          </div>
        );
        break;
      case Code.NotFound:
      case Code.InvalidArgument:
      case Code.PermissionDenied:
        body = <p className="text-slate-600 dark:text-slate-400">{t("ticketNotFound")}</p>;
        break;
      default:
        body = <p className="text-brand dark:text-brand-dark">{t("somethingWrong")}</p>;
    }
  } else {
    body = <p className="text-slate-600 dark:text-slate-400">{t("loading")}</p>;
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <Link to="/tickets" className="text-sm text-ink underline-offset-2 hover:underline dark:text-ink-dark">
        <span aria-hidden="true">← </span>
        {t("navTickets")}
      </Link>
      {body}
    </div>
  );
}

function TicketView({ ticket }: { ticket: Ticket }) {
  const { t, lang } = useI18n();
  // Lives here, not in CancelTicket, which unmounts once the ticket can't be cancelled.
  const [cancelOutcome, setCancelOutcome] = useState<"cancelled" | "cannotCancel" | null>(null);

  const trip = ticket.trip;
  const departs = Number(trip?.departsAtMs ?? 0n);
  const official = ticket.officialFare?.paisa ?? 0n;
  const charged = ticket.chargedFare?.paisa ?? 0n;
  const payment = paymentMethodLabels[ticket.paymentMethod];
  const cancelled = ticket.status === TicketStatus.CANCELLED;
  const money = (paisa: bigint | undefined) => formatPaisa(paisa ?? 0n, lang);

  return (
    <article className="space-y-5">
      <header className="rounded-xl border border-slate-200 p-5 text-center dark:border-slate-800">
        <h1 className="text-sm text-slate-600 dark:text-slate-400">{t("ticketCode")}</h1>
        <p className="mt-1 break-all font-mono text-3xl font-bold tracking-wider text-ink sm:text-4xl sm:tracking-widest dark:text-ink-dark">{ticket.code}</p>
        <div className="mt-2">
          <TicketStatusBadge status={ticket.status} />
        </div>
        {ticket.status === TicketStatus.CONFIRMED && ticket.qrPayload !== "" && (
          <TicketQr payload={ticket.qrPayload} code={ticket.code} />
        )}
      </header>

      {cancelOutcome === "cancelled" && cancelled && (
        <p role="status" className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
          {t("ticketCancelled", { refund: money(ticket.refund?.paisa) })}
        </p>
      )}
      {cancelOutcome === "cannotCancel" && (
        <p role="alert" className="text-sm text-brand dark:text-brand-dark">
          {t("cannotCancel")}
        </p>
      )}

      <dl className="divide-y divide-slate-100 rounded-xl border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800">
        {trip?.routeName?.[lang] && <Row label={t("route")}>{trip.routeName[lang]}</Row>}
        <Row label={t("from")}>{placeName(trip?.from, lang)}</Row>
        <Row label={t("to")}>{placeName(trip?.to, lang)}</Row>
        {departs > 0 && (
          <Row label={t("departure")}>
            {formatDate(departs, lang)} · {formatNepalTime(departs, lang)}
            {lang === "ne" && (
              <span className="mt-0.5 block text-xs font-normal text-slate-600 dark:text-slate-400">{t("dateAd", { date: formatAdDate(departs, lang) })}</span>
            )}
          </Row>
        )}
        <Row label={t("seat")}>{localDigits(ticket.seatNo, lang)}</Row>
        {trip?.operatorName?.[lang] && <Row label={t("operator")}>{trip.operatorName[lang]}</Row>}
        {trip && (
          <Row label={t("busClass")}>
            {t(vehicleClassLabels[trip.vehicleClass])}
            {trip.electric && ` · ${t("electric")}`}
          </Row>
        )}
        <Row label={t("officialFare")}>{money(official)}</Row>
        <Row label={t("chargedFare")}>
          {money(charged)}
          {charged === official && (
            <span className="mt-0.5 block text-xs font-normal text-emerald-700 dark:text-emerald-400">
              <span aria-hidden="true">✓ </span>
              {t("officialFareRespected")}
            </span>
          )}
        </Row>
        {payment && <Row label={t("paymentMethod")}>{t(payment)}</Row>}
        {ticket.passengerPhoneMasked && <Row label={t("phone")}>{localDigits(ticket.passengerPhoneMasked, lang)}</Row>}
        {cancelled && (
          <>
            <Row label={t("cancellationFee")}>{money(ticket.cancellationFee?.paisa)}</Row>
            <Row label={t("refund")}>{money(ticket.refund?.paisa)}</Row>
          </>
        )}
      </dl>

      {ticket.status === TicketStatus.CONFIRMED && <TripSafety ticket={ticket} />}

      {ticket.status === TicketStatus.CONFIRMED && ticket.cancellationIfNow && (
        <CancelTicket ticketId={ticket.id} quote={ticket.cancellationIfNow} onOutcome={setCancelOutcome} />
      )}

      <Link
        to="/complaints/new"
        search={{ ticket: ticket.code }}
        className="inline-block rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-900"
      >
        {t("reportProblem")}
      </Link>
    </article>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <dt className="text-slate-600 dark:text-slate-400">{label}</dt>
      <dd className="text-right font-medium">{children}</dd>
    </div>
  );
}

function TicketQr({ payload, code }: { payload: string; code: string }) {
  const { t } = useI18n();
  const qr = useMemo(() => qrPath(payload), [payload]);

  return (
    <figure className="mt-4">
      {/* Always dark on white: scanners struggle with inverted codes. */}
      <div className="mx-auto w-fit rounded-lg bg-white p-1">
        <svg
          viewBox={`0 0 ${qr.size} ${qr.size}`}
          className="h-60 w-60"
          role="img"
          aria-label={t("qrAlt", { code })}
          shapeRendering="crispEdges"
        >
          <rect width={qr.size} height={qr.size} fill="#fff" />
          <path d={qr.path} fill="#000" />
        </svg>
      </div>
      <figcaption className="mt-2 text-sm text-slate-600 dark:text-slate-400">{t("showQr")}</figcaption>
    </figure>
  );
}

const quietZone = 4;

/** Traces a QR code's dark modules as one SVG path, a horizontal run at a time. */
function qrPath(payload: string): { size: number; path: string } {
  const { modules } = createQr(payload, { errorCorrectionLevel: "M" });
  const parts: string[] = [];

  for (let row = 0; row < modules.size; row++) {
    let col = 0;
    while (col < modules.size) {
      if (!modules.get(row, col)) {
        col++;
        continue;
      }
      const start = col;
      while (col < modules.size && modules.get(row, col)) col++;
      parts.push(`M${start + quietZone} ${row + quietZone}h${col - start}v1h${start - col}z`);
    }
  }

  return { size: modules.size + 2 * quietZone, path: parts.join("") };
}

function CancelTicket(props: {
  ticketId: string;
  quote: CancellationQuote;
  onOutcome: (outcome: "cancelled" | "cannotCancel") => void;
}) {
  const { t, lang } = useI18n();
  const queryClient = useQueryClient();
  const refreshSession = useRefreshSession();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<MessageKey | null>(null);
  const cancel = useMutation(BookingService.method.cancelTicket);

  const refund = formatPaisa(props.quote.refund?.paisa ?? 0n, lang);
  const ticketKey = createConnectQueryKey({
    schema: BookingService.method.getTicket,
    input: { id: props.ticketId },
    cardinality: "finite",
  });

  function confirmCancel() {
    setError(null);
    cancel.mutate(
      { id: props.ticketId },
      {
        onSuccess: (res) => {
          if (res.ticket) {
            queryClient.setQueriesData({ queryKey: ticketKey }, create(GetTicketResponseSchema, { ticket: res.ticket }));
          }
          void queryClient.invalidateQueries({
            queryKey: createConnectQueryKey({ schema: BookingService.method.listMyTickets, cardinality: undefined }),
          });
          props.onOutcome("cancelled");
        },
        onError: (err) => {
          switch (errorCode(err)) {
            case Code.FailedPrecondition:
              props.onOutcome("cannotCancel");
              setConfirming(false);
              void queryClient.invalidateQueries({ queryKey: ticketKey });
              break;
            case Code.Unauthenticated:
              void refreshSession();
              break;
            default:
              setError("somethingWrong");
          }
        },
      },
    );
  }

  return (
    <section className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-800">
      <h2 className="font-semibold">{t("cancelTicket")}</h2>
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {t("cancelNowTerms", { percent: formatNumber(props.quote.feePercent, lang) })}
      </p>
      <dl className="text-sm">
        <div className="flex justify-between gap-4 py-1">
          <dt className="text-slate-600 dark:text-slate-400">{t("cancellationFee")}</dt>
          <dd className="font-medium">{formatPaisa(props.quote.fee?.paisa ?? 0n, lang)}</dd>
        </div>
        <div className="flex justify-between gap-4 py-1">
          <dt className="text-slate-600 dark:text-slate-400">{t("refund")}</dt>
          <dd className="font-medium">{refund}</dd>
        </div>
      </dl>

      {confirming ? (
        <div className="space-y-3 rounded-lg bg-red-50 p-3 dark:bg-red-950/40">
          <p className="text-sm font-medium">{t("cancelConfirm", { refund })}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={confirmCancel}
              disabled={cancel.isPending}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-60 dark:bg-brand-dark"
            >
              {cancel.isPending ? t("cancelling") : t("cancelYes")}
            </button>
            <button
              type="button"
              autoFocus
              onClick={() => setConfirming(false)}
              disabled={cancel.isPending}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:hover:bg-slate-900"
            >
              {t("cancelNo")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="rounded-md border border-brand px-4 py-2 text-sm font-medium text-brand hover:bg-red-50 dark:border-brand-dark dark:text-brand-dark dark:hover:bg-red-950/40"
        >
          {t("cancelTicket")}
        </button>
      )}

      {error && (
        <p role="alert" className="text-sm text-brand dark:text-brand-dark">
          {t(error)}
        </p>
      )}
    </section>
  );
}
